import {Badge, Modal} from '@mantine/core';
import {Table} from 'src/design/atoms/Table';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React, {useEffect, useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {
  useGetBadgeTemplateQuery,
  useGetBadgeTemplateVersionHistoryQuery,
  usePreviewBadgeTemplateMutation,
} from 'src/redux/queries/badgeTemplates';
import {BadgeTemplatePreviewFrame} from './BadgeTemplatePreviewFrame';

export interface BadgeTemplateVersionHistoryModalProps {
  opened: boolean;
  /** Target instance whose template is being viewed. */
  instanceId: number;
  /** Any version id belonging to the logical template. */
  templateId: number | undefined;
  templateName: string | undefined;
  onClose: () => void;
}

export const BadgeTemplateVersionHistoryModal: React.FC<
  BadgeTemplateVersionHistoryModalProps
> = ({opened, instanceId, templateId, templateName, onClose}) => {
  const [previewingId, setPreviewingId] = useState<number | undefined>();

  const {data: versions, isLoading} = useGetBadgeTemplateVersionHistoryQuery(
    {id: templateId as number, instanceId},
    {skip: !opened || templateId === undefined}
  );

  const {data: previewedTemplate, isFetching: isFetchingVersion} =
    useGetBadgeTemplateQuery(
      {id: previewingId as number, instanceId},
      {skip: previewingId === undefined}
    );

  const [previewBadgeTemplate, {data: previewResult, isLoading: isMerging}] =
    usePreviewBadgeTemplateMutation();

  useEffect(() => {
    if (previewedTemplate) {
      void previewBadgeTemplate({htmlContent: previewedTemplate.htmlContent});
    }
    // previewBadgeTemplate is a stable dispatch ref from RTK Query
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewedTemplate?.id]);

  useEffect(() => {
    if (!opened) {
      setPreviewingId(undefined);
    }
  }, [opened]);

  const handleClose = () => {
    setPreviewingId(undefined);
    onClose();
  };

  const previewingVersionNumber = versions?.find(
    v => v.id === previewingId
  )?.version;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Version History${templateName ? ` — ${templateName}` : ''}`}
      size="xl"
      styles={{title: {fontWeight: 700}}}
      data-testid="badge-template-version-history-modal">
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : (
        <Stack gap="md">
          <Table
            striped
            withTableBorder
            data-testid="badge-template-version-table">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Version</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(versions ?? []).map(version => (
                <Table.Tr key={version.id}>
                  <Table.Td>v{version.version}</Table.Td>
                  <Table.Td>
                    {version.createdAt
                      ? String(version.createdAt).toShortDate()
                      : '—'}
                  </Table.Td>
                  <Table.Td>
                    {version.activeVersion ? (
                      <Badge color="green" variant="light">
                        Active
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">
                        Archived
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => setPreviewingId(version.id)}>
                      Preview
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {previewingId !== undefined && (
            <Stack gap="xs" data-testid="badge-template-version-preview">
              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  Previewing version {previewingVersionNumber ?? ''}
                </Text>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => setPreviewingId(undefined)}>
                  Close preview
                </Button>
              </Group>
              <BadgeTemplatePreviewFrame
                html={previewResult?.html}
                isLoading={isFetchingVersion || isMerging}
              />
            </Stack>
          )}
        </Stack>
      )}
    </Modal>
  );
};
